import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaFileInvoiceDollar, FaCheckCircle, FaTimes, FaPrint, FaDownload, FaGraduationCap, FaTimesCircle } from "react-icons/fa";
import { paymentService } from "../../services/paymentService";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";

const PaymentReceipt = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [receiptData, setReceiptData] = useState(null);
    const [fetchingReceipt, setFetchingReceipt] = useState(false);

    useEffect(() => {
        const paymentId = searchParams.get("paymentId");
        if (paymentId) {
            fetchReceiptData(paymentId);
        } else {
            toast.error("Payment ID not found");
            navigate("/payments", { replace: true });
        }
    }, [searchParams, navigate]);

    const fetchReceiptData = async (paymentId) => {
        try {
            setLoading(true);
            const response = await paymentService.getReceiptData(paymentId);
            if (response.success) {
                setReceiptData(response.data);
            } else {
                toast.error("Failed to load receipt data");
                navigate("/payments", { replace: true });
            }
        } catch (error) {
            console.error("Error fetching receipt:", error);
            toast.error("Failed to load receipt");
            navigate("/payments", { replace: true });
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printStyles = `
            @media print {
                html, body { 
                    margin: 0 !important; 
                    padding: 0 !important; 
                    background: white !important;
                    -webkit-print-color-adjust: exact;
                }
                body * { visibility: hidden !important; }
                #printable-content, #printable-content * { visibility: visible !important; }
                #printable-content { 
                    position: static !important;
                    width: calc(100% - 20mm) !important; 
                    margin: 10mm auto !important; 
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                }
                .print-compact-section { 
                    margin-bottom: 12px !important; 
                    padding-bottom: 8px !important; 
                }
                .print-compact-row { 
                    padding-top: 4px !important; 
                    padding-bottom: 4px !important; 
                    font-size: 10pt !important;
                }
                @page { margin: 0; size: auto; }
            }
        `;
        const styleSheet = document.createElement("style");
        styleSheet.innerText = printStyles;
        document.head.appendChild(styleSheet);
        window.print();
        document.head.removeChild(styleSheet);
    };

    const handleDownloadPDF = async () => {
        const element = document.getElementById('printable-content');
        if (!element) return;

        const originalStyle = element.style.cssText;
        element.style.width = '800px';
        element.style.boxShadow = 'none';
        element.style.margin = '0 auto';

        const opt = {
            margin: 10,
            filename: `Receipt_${receiptData?.receiptNo || 'payment'}.pdf`,
            image: { type: 'jpeg', quality: 1.0 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                letterRendering: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: 800,
                scrollY: -window.scrollY,
                windowWidth: 800
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        try {
            setFetchingReceipt(true);
            const toastId = toast.loading("Generating PDF...");
            await html2pdf().from(element).set(opt).save();
            toast.dismiss(toastId);
            toast.success("PDF Downloaded successfully");
        } catch (error) {
            console.error("PDF generation error:", error);
            toast.error("Failed to generate PDF");
        } finally {
            element.style.cssText = originalStyle;
            setFetchingReceipt(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pragyaBlue mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading receipt...</p>
                </div>
            </div>
        );
    }

    if (!receiptData) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Receipt Not Found</h2>
                    <p className="text-slate-600 mb-4">Unable to load receipt data.</p>
                    <button
                        onClick={() => navigate("/payments")}
                        className="px-6 py-2 bg-pragyaBlue text-white rounded-lg hover:bg-opacity-90"
                    >
                        Back to Payments
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="max-w-5xl mx-auto">
                {/* Header with Actions */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pragyaBlue rounded-lg flex items-center justify-center shadow-lg shadow-pragyaBlue/20">
                            <FaFileInvoiceDollar className="text-lg text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg">Payment Receipt</h1>
                            <p className="text-xs text-slate-400 font-mono">{receiptData.receiptNo}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadPDF}
                            disabled={fetchingReceipt}
                            className="flex items-center gap-2 bg-pragyaBlue hover:bg-opacity-90 px-4 py-2 rounded-lg text-xs font-black transition-all shadow-lg active:scale-95 disabled:opacity-50 text-white"
                        >
                            <FaDownload /> {fetchingReceipt ? "GENERATING..." : "DOWNLOAD PDF"}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="hidden sm:flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 border border-slate-700 text-white"
                        >
                            <FaPrint /> PRINT
                        </button>
                        <button
                            onClick={() => navigate("/payments")}
                            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600 hover:text-slate-800"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Receipt Content */}
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-2 sm:p-8 print:p-2 print:bg-white">
                        <div id="printable-content" className="bg-white w-full max-w-[800px] mx-auto shadow-2xl rounded-sm print:shadow-none print:max-w-none print:m-0 print:p-0">
                            <div className="border-[6px] border-double border-slate-200 p-4 sm:p-8 relative overflow-hidden bg-white print:p-2 print:border-none print-no-padding">
                                {/* School Header */}
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 pb-6 border-b-2 border-slate-100 relative z-10 print-compact-section">
                                    <div className="flex flex-col items-center sm:items-start text-center sm:text-left">
                                        {receiptData.school?.logoUrl ? (
                                            <img src={receiptData.school.logoUrl} alt="School Logo" className="h-14 mb-2 object-contain" />
                                        ) : (
                                            <div className="w-14 h-14 bg-pragyaBlue rounded-xl flex items-center justify-center mb-2">
                                                <FaGraduationCap className="text-2xl text-white" />
                                            </div>
                                        )}
                                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight line-height-1.2">{receiptData.school?.name}</h2>
                                        <p className="text-[10px] font-bold text-pragyaBlue uppercase tracking-[0.2em]">{receiptData.school?.tagLine}</p>
                                    </div>
                                    <div className="text-center sm:text-right text-[10px] text-slate-500 space-y-0.5">
                                        <p className="font-semibold text-slate-700">{receiptData.school?.address?.line1}</p>
                                        <p>{receiptData.school?.address?.city}, {receiptData.school?.address?.district?.name}</p>
                                        <p>{receiptData.school?.address?.state?.name} - {receiptData.school?.address?.pinCode}</p>
                                        <p className="pt-1 font-bold text-slate-800">Email: {receiptData.school?.contact?.email}</p>
                                        <p className="font-bold text-slate-800">Phone: {receiptData.school?.contact?.phone}</p>
                                    </div>
                                </div>

                                {/* Receipt Info & Student Info Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 print-compact-section">
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <h4 className="text-[9px] uppercase font-black text-slate-400 mb-2 tracking-widest border-b border-slate-200 pb-1">Receipt Details</h4>
                                        <div className="space-y-1.5">
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Receipt No:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.receiptNo}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Date:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{new Date(receiptData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Type:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.paymentType}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <h4 className="text-[9px] uppercase font-black text-slate-400 mb-2 tracking-widest border-b border-slate-200 pb-1">Student Info</h4>
                                        <div className="space-y-1.5">
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Name:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.student?.name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Class:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.student?.class}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Roll No:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.student?.rollNo}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[10px] text-slate-500">Section:</span>
                                                <span className="text-[10px] font-bold text-slate-900">{receiptData.student?.section}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Fee Breakdown Table */}
                                <div className="mb-6 overflow-hidden border border-slate-200 rounded-xl print-compact-section">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-900 text-white">
                                                <th className="px-4 py-2 text-[9px] font-black uppercase tracking-widest">S.No</th>
                                                <th className="px-4 py-2 text-[9px] font-black uppercase tracking-widest">Description</th>
                                                <th className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-right">Amount (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {receiptData.paymentDetails?.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50">
                                                    <td className="px-4 py-1.5 text-[10px] font-medium text-slate-500 print-compact-row">{idx + 1}</td>
                                                    <td className="px-4 py-1.5 text-[10px] font-bold text-slate-800 print-compact-row">
                                                        <div className="flex flex-col">
                                                            <span>{item.head}</span>
                                                            {item.date && (
                                                                <span className="text-[8px] text-slate-400 font-medium">Paid: {new Date(item.date).toLocaleDateString()}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-1.5 text-[10px] font-black text-slate-900 text-right print-compact-row">₹{item.amount.toLocaleString('en-IN')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-slate-50 border-t-2 border-slate-200">
                                                <td colSpan="2" className="px-4 py-2 text-[10px] font-black text-slate-900 text-right uppercase">Sub Total</td>
                                                <td className="px-4 py-2 text-[10px] font-black text-slate-900 text-right">₹{(receiptData.summary?.totalAmount || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            {(receiptData.summary?.discountAmount || 0) > 0 && (
                                                <tr className="bg-white border-t border-slate-100">
                                                    <td colSpan="2" className="px-4 py-2 text-[10px] font-bold text-green-600 text-right uppercase">Discount</td>
                                                    <td className="px-4 py-2 text-[10px] font-black text-green-600 text-right">-₹{(receiptData.summary?.discountAmount || 0).toLocaleString('en-IN')}</td>
                                                </tr>
                                            )}
                                            <tr className="bg-white border-t border-slate-100">
                                                <td colSpan="2" className="px-4 py-2 text-[10px] font-bold font-black text-slate-900 text-right uppercase">Total</td>
                                                <td className="px-4 py-2 text-[10px] font-black text-slate-900 text-right">₹{(Number(receiptData.summary?.totalAmount) - Number(receiptData.summary?.discountAmount) || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            <tr className="bg-pragyaBlue text-white">
                                                <td colSpan="2" className="px-4 py-2.5 text-xs font-black text-right uppercase tracking-widest leading-none">Net Paid</td>
                                                <td className="px-4 py-2.5 text-base font-black text-right leading-none">₹{(receiptData.summary?.paidAmount || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            {(receiptData.summary?.remainingBalance || 0) > 0 && (
                                                <tr className="bg-amber-50 border-t border-amber-100">
                                                    <td colSpan="2" className="px-4 py-2 text-[10px] font-bold text-amber-700 text-right uppercase">Remaining Balance</td>
                                                    <td className="px-4 py-2 text-[10px] font-black text-amber-700 text-right">₹{(receiptData.summary?.remainingBalance || 0).toLocaleString('en-IN')}</td>
                                                </tr>
                                            )}
                                        </tfoot>
                                    </table>
                                </div>

                                {/* Payment Method & Transaction */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 print-compact-section">
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <FaCheckCircle className="text-green-500 text-sm" />
                                                <div>
                                                    <p className="text-[8px] text-slate-400 font-bold uppercase">Transaction ID</p>
                                                    <p className="text-[10px] font-black text-slate-800">{receiptData.summary?.transactionId || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-4 h-4 rounded-full bg-pragyaBlue/20 flex items-center justify-center text-[6px] text-pragyaBlue font-bold">●</div>
                                                <div>
                                                    <p className="text-[8px] text-slate-400 font-bold uppercase">Method</p>
                                                    <p className="text-[10px] font-black text-slate-800">{receiptData.summary?.paymentMethod || 'UPI'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-end items-center sm:items-end p-2">
                                        <div className="text-center w-32">
                                            {receiptData.school?.sealUrl ? (
                                                <img src={receiptData.school.sealUrl} alt="School Seal" className="h-14 mx-auto mb-1 opacity-80" />
                                            ) : (
                                                <div className="h-14 w-14 border-2 border-dashed border-slate-200 rounded-full mx-auto mb-1 flex items-center justify-center text-[8px] text-slate-300 font-bold italic">SEAL</div>
                                            )}
                                            <div className="border-t border-slate-900 pt-1">
                                                <p className="text-[8px] font-black text-slate-900 uppercase">Authorized Signatory</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer Message */}
                                <div className="text-center border-t border-slate-100 pt-6">
                                    <p className="text-[9px] font-black text-slate-900 uppercase tracking-widest mb-0.5 italic">Computer Generated Receipt</p>
                                    <p className="text-[8px] font-bold text-slate-400 italic">No signature required for digital verification.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back Button */}
                <div className="mt-4 text-center">
                    <button
                        onClick={() => navigate("/payments")}
                        className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                        Back to Payments
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentReceipt;
